<template>
	<div
		v-if="showComponent"
		class="ecommerce-card-wrapper"
		:class="backgroundColor"
		:style="`padding: ${padding}; background-color: ${bgColor};`"
	>
		<TnSkeleton
			width="100%"
			height="100%"
			v-if="loading"
			class="skeleton"
			:style="`margin: -${padding}`"
			border-radius="s"
		/>
		<div
			ref="componentsRef"
			class="slots"
			:style="`gap: ${spacing}px; align-items: ${alignItems};`"
		>
			<slot>
				<component
					v-for="(component, i) in components"
					:key="i"
					:is="resolveComponent(component.template.ref.split('/').pop())"
					:class="loading ? 'loading' : ''"
					:component="component"
				/>
			</slot>
		</div>
	</div>
</template>
<script setup lang="ts">
import componentList from "~~/gizmo-components";

interface Component {
	template: {
		ref: string;
	};
}

const {
	components = [],
	spacing = 0,
	padding = "0",
	alignItems = "flex-start",
	staticComponent = false,
	bgColor = "white",
} = defineProps<{
	components?: Component[];
	spacing?: number;
	padding?: string;
	alignItems?: string;
	staticComponent?: boolean;
	bgColor?: string;
}>();

const backgroundColor = useBackgroundColor(bgColor);

const loading = ref<boolean>(true);
const componentsMounted = ref<boolean>(false);
const componentsRef = ref<HTMLElement | null>(null);

const showComponent = computed(() => {
	if (!loading.value && componentsMounted.value === false && !staticComponent) {
		return false;
	}
	return true;
});

const resolveComponent = (componentName: string) => {
	const key = Object.keys(componentList).find((key) => key.toLowerCase() === componentName.toLowerCase());
	if (key) return componentList[key];
};

const getChildNodes = () => {
	if (!componentsRef.value) return [];
	return Array.from(componentsRef.value.childNodes).filter(
		(node) => node.nodeName !== "#comment" && node.nodeName !== "#text",
	);
};

const initializeComponent = () => {
	if (components && components.length > 0) {
		const mo = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (
					Array.from(mutation.addedNodes).filter((n) => n.nodeName !== "#comment" && n.nodeName !== "#text").length > 0
				) {
					componentsMounted.value = true;
					mo.disconnect();
				}
			});
			loading.value = false;
		});
		if (componentsRef.value) {
			mo.observe(componentsRef.value, {
				childList: true,
				subtree: true,
				attributes: true,
			});
			// Initial check
			const hasInitialContent = getChildNodes();
			if (hasInitialContent.length > 0) {
				componentsMounted.value = true;
				mo.disconnect();
				loading.value = false;
			}
			setTimeout(() => {
				if (loading.value && !componentsMounted.value && !(getChildNodes()?.length > 0)) {
					loading.value = false;
				}
			}, 1000);
		}
	}
};

onMounted(() => {
	if (staticComponent) {
		loading.value = false;
	} else {
		initializeComponent();
	}
});
</script>
<style lang="scss" scoped>
.ecommerce-card-wrapper {
	border-radius: $spacing-s;
	border: 1px solid $color-neutrals-100-tint;
	position: relative;
	width: 100%;
}

.slots {
	display: flex;
	flex-direction: column;
}

.skeleton {
	position: absolute;
}

.loading {
	opacity: 0;
}
</style>
